import { useCallback, useEffect, useState } from 'react';
import { useMediaQuery } from '@providers/MediaQueryProvider';
import { useTranslate } from '@providers/TranslateProvider';
import { useLocation, useNavigate } from '@tanstack/react-router';

import { ChessBoard } from '@exports/ChessBoard';
import { Scoresheet, ScoresheetSkeleton } from '@exports/Scoresheet';
import { FEN_STARTING_POSITION } from '@libs/chess/constants';
import { cx } from '@libs/classnames';

import { useAuth } from '@features/auth/hooks/useAuth';
import { ErrorPage } from '@features/auth/pages/ErrorPage';
import { NotFoundPage } from '@features/auth/pages/NotFoundPage';
import { TIME_PRESSURE } from '@features/game/constants';
import { useFastGame } from '@features/game/hooks/useFastGame';
import { useFriendGameCtx } from '@features/game/providers/FriendGameProvider';
import { IGame } from '@features/game/types/game';
import { DrawOffer } from '@features/game/ui/DrawOffer';
import { GameButtonsSkeleton } from '@features/game/ui/GameButtonsSkeleton/GameButtonsSkeleton';
import { GameFinishedModal } from '@features/game/ui/GameFinishedModal/GameFinishedModal';
import { GameInfo } from '@features/game/ui/GameInfo/GameInfo';
import { GameLayout, GameLayoutSkeleton } from '@features/game/ui/GameLayout/GameLayout';
import { PlayerPanelWidget } from '@features/game/ui/PlayerPanel';
import { PlayerPanelSkeleton } from '@features/game/ui/PlayerPanelSkeleton';
import { formatGameSpecs } from '@features/game/utils/formatGameSpecs';
import { getGamePgnFilename, mapGameToPgn } from '@features/game/utils/gamePgnUtils';
import { ServerGameWidget } from '@features/game/widgets/ServerGameWidget';
import { IValidRoute } from '@features/shared/utils/router';
import { UserAuthMenuWidget } from '@features/user/ui/UserAuthMenu/UserAuthMenuWidget';
import { UserSettingsMenuWidget } from '@features/user/ui/UserSettingsMenu/UserSettingsMenuWidget';

import { Analysis } from '../AnalysisPage';
import { FriendGameActions } from './components/GameActions/FriendGameActions';
import { GuestModal } from './components/GuestModal/GuestModal';
import { HostModal } from './components/HostModal/HostModal';

import styles from './FriendGamePageWidget.module.css';

/*
    ____,-------------------------------,____
    \   |             Page              |   /
    /___|-------------------------------|___\
*/

type Props = {
  key: string /* sic! Для разных игр необходимы разные ключи */;
  gameId: string;
  isView?: boolean;
};

const BACK_LINK: IValidRoute = '/game/friend';

export function FriendGamePageWidget({ gameId, isView }: Props) {
  const navigate = useNavigate();
  const { t } = useTranslate();
  const mobile = useMediaQuery() !== 'desktop';
  const { isAuthenticated, user } = useAuth();
  const fg = useFriendGameCtx();

  const customBackUrl = useLocation().state.backUrl;

  const {
    game,
    isGamePending,
    onRefetchGame,
    //
    isInProgress,
    isFinished,
    //
    side,
    playerSide,
    isPlayer,
    //
    rotated,
    boardOrientation,
    player1Color,
    player2Color,
    //
    toast,
    onError,
    //
    canRequestDraw,
    showDrawOffer,
    onDrawAccept,
    onDrawDecline,
    onDrawOffer,
    //
    canResign,
    onResign,
    //
    canClaimVictory,
    onClaimVictory,
    //
    isPlayer1Offline,
    isPlayer2Offline,
    onNetworkProblems,
    //
    rematchStatus,
    onRematch,
    //
    gameSpecs,
    //
    scoresheet,
    customPlayersTimeMs,
    historyMove,
    //
    arrows,
    onUserArrow,
    //
    modal,
    //
    canShowAnalysis,
    showAnalysis,
    onOpenAnalysis,
    onCloseAnalysis,
    //
    isTopFirstMove,
    isBottomFirstMove,
    //
    boardTheme,
    autoPromotion,
    boardSounds,
  } = useFastGame(fg.playerId, fg.game);

  const isWaiting = fg.isLoading || fg.invite?.status === 'New';
  const isExpired = fg.invite?.status === 'Deleted' || fg.invite?.status === 'Expired';
  const isHost = fg.invite?.host?.userId === fg.playerId;

  const [finishedDismissed, setFinishedDismissed] = useState(false);

  // редирект на рематч
  useEffect(() => {
    if (fg.invite?.rematchInviteId && fg.invite.status !== 'Deleted') {
      navigate({
        to: '/game/friend/$gameId',
        params: { gameId: fg.invite?.rematchInviteId },
        replace: true,
      });
    }
  }, [fg.invite?.rematchInviteId, fg.invite?.status, navigate]);

  const onReturn = useCallback(() => {
    // HACK: из-за неудачной реализации подразделов ЛК (закрываются недетерминированно по useGoBack),
    // безопасней использовать history.back()
    customBackUrl ? history.back() : navigate({ to: BACK_LINK });
  }, [customBackUrl, navigate]);
  const onReturnToFriend = useCallback(() => navigate({ to: BACK_LINK }), [navigate]);

  if (isExpired || fg.error?.isForbidden || fg.error?.isNotFound) {
    return <NotFoundPage subTitle={'Приглашение не найдено или срок его действия истек'} />;
  }

  if (fg.game?.customTokenError) {
    return <ErrorPage subTitle={'Не удалось подключиться к игре. Обновите страницу'} />;
  }

  if (!fg.isLoading && isWaiting) {
    const hostIsWhite = fg.invite?.hostSide === 'White';
    const host = {
      id: fg.invite!.host!.userId,
      firstName: fg.invite!.host!.firstName,
      lastName: fg.invite!.host!.lastName,
    } as IGame['black'];
    const guest = {
      id: fg.playerId,
      firstName: user ? user.firstName : 'Вы',
      lastName: user ? user.lastName : undefined,
    } as IGame['black'];
    const isRating = Boolean(isAuthenticated && fg.invite?.ratingGame);
    const time = fg.invite!.clockControlSettings.initialSeconds * 1000;
    const fauxGame: IGame = {
      black: hostIsWhite ? guest : host,
      white: hostIsWhite ? host : guest,
      whiteClockControlSettings: fg.invite!.clockControlSettings,
      blackClockControlSettings: fg.invite!.clockControlSettings,
      id: gameId,
      result: 'NotStarted',
      nextMoveBy: 'White',
      status: 'NotStarted',
      clockControlType: fg.invite!.clockControlType,
      clock: { timestampMs: Date.now(), whiteLeftMs: time, blackLeftMs: time },
      noRatingChange: !isRating,
    } as IGame;
    const fauxSpecs = formatGameSpecs(fauxGame, hostIsWhite ? 'black' : 'white', t);

    return (
      <>
        <GameLayout
          backLink={customBackUrl ?? BACK_LINK}
          onBackClick={onReturn}
          header={
            <GameInfo
              title="Игра с другом"
              specs={fauxSpecs}
              isRating={isRating}
              mobileBackLink={customBackUrl ?? BACK_LINK}
              twoAvatars
              onMobileBackClick={onReturn}
            />
          }
          board={
            <ChessBoard
              theme={boardTheme}
              position={FEN_STARTING_POSITION}
              orientation={
                isHost ? (hostIsWhite ? 'white' : 'black') : hostIsWhite ? 'black' : 'white'
              }
              notation={!mobile ? undefined : 'inside'}
            />
          }
          player1={
            !isHost ? (
              <PlayerPanelWidget
                color={hostIsWhite ? 'white' : 'black'}
                orientation="top"
                game={fauxGame}
              />
            ) : (
              <PlayerPanelSkeleton orientation="top" />
            )
          }
          player2={
            hostIsWhite || isHost ? (
              <PlayerPanelWidget
                color={isHost ? (hostIsWhite ? 'white' : 'black') : hostIsWhite ? 'black' : 'white'}
                orientation="bottom"
                game={fauxGame}
              />
            ) : (
              <PlayerPanelSkeleton orientation="bottom" />
            )
          }
          scoresheet={!mobile ? <ScoresheetSkeleton /> : undefined}
          avatar={
            <>
              <UserSettingsMenuWidget />
              <UserAuthMenuWidget alwaysRound />
            </>
          }
          actions={<GameButtonsSkeleton />}
        />

        {isHost ? (
          <HostModal className={styles.modal} isMobile={mobile} link={window.location.href} />
        ) : (
          <GuestModal
            className={styles.modal}
            user={fg.invite!.host!}
            specs={fauxSpecs}
            isAuthenticated={isAuthenticated}
            myGamesCount={fg.myGamesCount}
            onJoin={fg.accept}
          ></GuestModal>
        )}
      </>
    );
  }

  if (fg.isLoading || fg.game?.isLoading || isGamePending || !game) {
    return <GameLayoutSkeleton />;
  }

  if (showAnalysis) {
    return (
      <Analysis
        avatar={
          <>
            <UserSettingsMenuWidget />
            <UserAuthMenuWidget alwaysRound />
          </>
        }
        twoAvatars
        initialPgn={mapGameToPgn(game)}
        filename={getGamePgnFilename(game)}
        initialOrientation={side}
        isRealGameAnalysis
        onClose={onCloseAnalysis}
      />
    );
  }

  const showFinishedModal = !isView && isFinished && !finishedDismissed && !showAnalysis;

  return (
    <>
      {showFinishedModal && playerSide !== 'none' && (
        <GameFinishedModal
          game={game}
          side={playerSide}
          isMobile={mobile}
          position="centered"
          rematch={rematchStatus}
          returnText="Создать новую игру"
          onRematch={onRematch}
          onAnalysis={canShowAnalysis ? onOpenAnalysis : undefined}
          onClose={() => setFinishedDismissed(true)}
          onReturn={onReturnToFriend}
        />
      )}
      <GameLayout
        backLink={customBackUrl ?? BACK_LINK}
        onBackClick={onReturn}
        board={
          <ServerGameWidget
            key={rotated ? 'r' : 'o'}
            className={cx({
              [styles.is_rotate_animated]: rotated !== null || showAnalysis !== null,
            })}
            mode={isPlayer && isInProgress ? 'fast-game' : 'spectator'}
            game={game}
            playerId={fg.playerId}
            anonToken={fg.game?.customToken}
            historyMove={historyMove}
            onError={onError}
            onRefetch={onRefetchGame}
            onNetworkProblems={onNetworkProblems}
            //
            allowPremove
            autoPromotion={autoPromotion}
            boardSounds={boardSounds}
            //
            orientation={boardOrientation}
            theme={boardTheme}
            notation={mobile ? 'inside' : undefined}
            modal={modal}
            arrows={arrows}
            onUserArrow={onUserArrow}
          />
        }
        avatar={
          <>
            <UserSettingsMenuWidget />
            <UserAuthMenuWidget alwaysRound />
          </>
        }
        header={
          <GameInfo
            title="Игра с другом"
            specs={gameSpecs}
            isRating={game?.noRatingChange === false}
            mobileBackLink={customBackUrl ?? BACK_LINK}
            twoAvatars
            onMobileBackClick={onReturn}
          />
        }
        errorToast={toast}
        popup={
          showDrawOffer && fg.playerId ? (
            <DrawOffer onAccept={onDrawAccept} onDecline={onDrawDecline} />
          ) : undefined
        }
        player1={
          <PlayerPanelWidget
            orientation="top"
            color={player1Color}
            game={game}
            mobile={mobile}
            timePressureSec={TIME_PRESSURE}
            scoresheetTimeMs={customPlayersTimeMs}
            isFirstMoveTime={isTopFirstMove}
            networkProblem={isPlayer1Offline}
          />
        }
        player2={
          <PlayerPanelWidget
            orientation="bottom"
            color={player2Color}
            game={game}
            mobile={mobile}
            timePressureSec={TIME_PRESSURE}
            scoresheetTimeMs={customPlayersTimeMs}
            isFirstMoveTime={isBottomFirstMove}
            networkProblem={isPlayer2Offline}
          />
        }
        scoresheet={
          <Scoresheet
            {...scoresheet.props}
            enableGlobalShortcuts
            customMessage={rematchStatus === 'opponent' ? 'Соперник предлагает реванш…' : undefined}
          />
        }
        actions={
          <FriendGameActions
            isPlaying={isPlayer && isInProgress}
            isFirstMove={isBottomFirstMove || isTopFirstMove}
            isRating={!game.noRatingChange}
            canRequestDraw={canRequestDraw}
            canResign={canResign}
            canClaimVictory={canClaimVictory}
            canAnalyze={canShowAnalysis}
            canRematch={rematchStatus !== 'none'}
            rematchWaiting={rematchStatus === 'player'}
            rematchOpponentOffered={rematchStatus === 'opponent'}
            onRematch={onRematch}
            onCancelGame={fg.game?.reject}
            onDraw={onDrawOffer}
            onResign={onResign}
            onClaimVictory={onClaimVictory}
            onAnalize={onOpenAnalysis}
            onReturn={onReturnToFriend}
          />
        }
      />
    </>
  );
}
